import React, { useMemo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Switch, Space, Typography, Modal } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useDriverData } from '../../../features/driverData/queries/useDriverData'
import { useUpdateDriverData } from '../../../features/driverData/queries/useUpdateDriverData'
import { useCtxDimensions } from '../../../features/dimension/queries/useCtxDimensions'
import { useCtxBudgetDrivers } from '../../../features/budgetDrivers/queries/useCtxBudgetDrivers'
import { filtersSelector } from '../../../redux/context/filters/selectors'
import { generateDriverDataMatrix, mergeDriverData } from '../../../features/driverData/utils/generateDriverDataMatrix'
import DriverDataTable from '../../../features/driverData/components/DriverDataTable'
import { FormulaMonthlyBudget } from '../../../features/driverData/types/driverData'
import PageHeaderWrapper from '../../../components/PageHeaderWrapper/PageHeaderWrapper'
import { Dimension } from '../../../features/financialStatements'

const { Text } = Typography

const ROW_LIMIT_WARNING = 100000

interface DriversPageLocationState {
  formulaIds?: number[]
  dimensionIds?: string[]
}

const DriversPage: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { dimensions: contextDimensions } = useSelector(filtersSelector)
  const { formulaIds, dimensionIds: stateDimensionIds } = (location.state as DriversPageLocationState | null) || {}
  const [showAll, setShowAll] = useState(false)

  // Fetch driver data from API
  const { data: apiData = [], isLoading: isLoadingApi } = useDriverData()

  // Update driver data mutation
  const { mutateAsync: updateDriverData } = useUpdateDriverData()

  // Save handler - accepts pre-converted formula data
  const handleSave = useCallback(
    async (formulaData: FormulaMonthlyBudget[]) => {
      await updateDriverData(formulaData)
    },
    [updateDriverData]
  )

  // Fetch budget drivers and dimensions for matrix generation
  const { data: dimensions = [], isLoading: isLoadingDimensions } = useCtxDimensions()
  const { data: budgetDrivers = [], isLoading: isLoadingBudgetDrivers } = useCtxBudgetDrivers()

  // Handle "show all" toggle with warning for large datasets
  const handleShowAllChange = useCallback(
    (checked: boolean) => {
      if (!checked) {
        // Turning off "show all" - no warning needed
        setShowAll(false)
        return
      }

      // Calculate how many rows would be generated
      const totalPossibleRows = budgetDrivers.length * (dimensions.length + 1) // +1 for top-level dimension

      if (totalPossibleRows > ROW_LIMIT_WARNING) {
        // Show warning modal
        Modal.confirm({
          title: 'Suuri määrä dataa',
          icon: <ExclamationCircleOutlined />,
          content: `Olet aikeissa generoida ${totalPossibleRows.toLocaleString()} riviä. Tämä voi aiheuttaa hitautta tai kaataa selaimen. Haluatko jatkaa?`,
          okText: 'Jatka',
          cancelText: 'Peruuta',
          onOk: () => {
            setShowAll(true)
          }
        })
      } else {
        setShowAll(true)
      }
    },
    [budgetDrivers.length, dimensions.length, t]
  )

  // Prepare initial filters from location state or context
  const initialFilters = useMemo(
    () => ({
      // Prioritize dimensionIds from navigation state, then context, then default to top-level
      dimensionIds:
        stateDimensionIds || (contextDimensions && contextDimensions.length > 0 ? contextDimensions : ['null']),
      formulaIds
    }),
    [contextDimensions, stateDimensionIds, formulaIds]
  )

  // Generate complete matrix and merge with API data
  const driverData = useMemo(() => {
    if (budgetDrivers.length === 0) return []

    // Add top-level dimension manually (enterprise level)
    const topLevelDimension: Dimension = {
      id: 0,
      dimensionId: 'null',
      companyId: '',
      name: t('global:enterpriseLevel'),
      order: -1
    }
    const dimensionsWithTopLevel = [topLevelDimension, ...dimensions]

    // OPTIMIZATION: Generate matrix ONLY for filtered formulas and dimensions (unless "show all" is enabled)
    // This prevents creating millions of unnecessary rows
    let formulasToGenerate = budgetDrivers.map(driver => driver.formula)
    let dimensionsToGenerate = dimensionsWithTopLevel

    // Only apply initialFilters if "show all" is NOT enabled
    if (!showAll) {
      // Filter formulas if initialFilters.formulaIds is set
      if (initialFilters.formulaIds && initialFilters.formulaIds.length > 0) {
        const formulaIdSet = new Set(initialFilters.formulaIds)
        formulasToGenerate = formulasToGenerate.filter(f => formulaIdSet.has(f.id))
      }

      // Filter dimensions if initialFilters.dimensionIds is set
      if (initialFilters.dimensionIds && initialFilters.dimensionIds.length > 0) {
        const dimensionIdSet = new Set(initialFilters.dimensionIds)
        dimensionsToGenerate = dimensionsToGenerate.filter(d => dimensionIdSet.has(d.dimensionId))
      }
    }

    // Generate matrix only for filtered formulas and dimensions
    const matrix = generateDriverDataMatrix(formulasToGenerate, dimensionsToGenerate)
    const mergedData = mergeDriverData(matrix, apiData)

    return mergedData
  }, [budgetDrivers, dimensions, apiData, t, initialFilters, showAll])

  const isLoading = isLoadingApi || isLoadingDimensions || isLoadingBudgetDrivers

  const toolbarLeftContent = (
    <Space>
      <Switch checked={showAll} onChange={handleShowAllChange} />
      <Text>{t('driversPage:show-all')}</Text>
    </Space>
  )

  return (
    <PageHeaderWrapper title={t('menu:/budgeting/drivers')}>
      <div style={{ height: 'calc(100vh - 150px)', width: '100%' }}>
        <DriverDataTable
          data={driverData}
          isLoading={isLoading}
          initialFilters={initialFilters}
          toolbarLeftContent={toolbarLeftContent}
          onSave={handleSave}
        />
      </div>
    </PageHeaderWrapper>
  )
}

export default DriversPage
